import { BarChartOutlined, LineChartOutlined, BuildOutlined, ToolOutlined } from "@ant-design/icons";
import type { ChartTemplateKey } from "./PlotConfigurator/plotState";

export interface ChartTemplateOption {
  key: ChartTemplateKey | "scratch";
  label: string;
  description: string;
  icon: React.ReactNode;
  preview: React.ReactNode;
}

const BAR_COLOR = "#1a73e8";
const BAR_COLOR_ALT = "#0d9f6e";
const LINE_COLOR = "#e8561a";
const STACK_COLORS = ["#1a73e8", "#0d9f6e", "#9333ea"];

function MiniBarPreview() {
  const bars = [0.6, 0.85, 0.45, 0.7, 0.55, 0.9, 0.5];
  return (
    <svg viewBox="0 0 80 36" className="chart-tpl-svg">
      {bars.map((h, i) => (
        <rect key={i} x={2 + i * 11} y={36 - h * 32} width={8} height={h * 32} rx={1.5} fill={BAR_COLOR} />
      ))}
    </svg>
  );
}

function MiniLinePreview() {
  const linePoints = [0.4, 0.55, 0.48, 0.72, 0.62, 0.85, 0.7];
  const pts = linePoints.map((h, i) => `${6 + i * 11},${36 - h * 32}`).join(" ");
  return (
    <svg viewBox="0 0 80 36" className="chart-tpl-svg">
      <polyline points={pts} fill="none" stroke={BAR_COLOR} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      {linePoints.map((h, i) => (
        <circle key={i} cx={6 + i * 11} cy={36 - h * 32} r={2} fill={BAR_COLOR} />
      ))}
    </svg>
  );
}

function MiniDualBarPreview() {
  const pairs: [number, number][] = [
    [0.55, 0.4],
    [0.75, 0.5],
    [0.45, 0.6],
    [0.65, 0.45],
    [0.55, 0.7],
    [0.85, 0.55],
    [0.5, 0.65],
  ];
  return (
    <svg viewBox="0 0 80 36" className="chart-tpl-svg">
      {pairs.map(([a, b], i) => {
        const groupX = 2 + i * 11;
        return (
          <g key={i}>
            <rect x={groupX} y={36 - a * 32} width={4} height={a * 32} rx={1} fill={BAR_COLOR} />
            <rect x={groupX + 5} y={36 - b * 32} width={4} height={b * 32} rx={1} fill={BAR_COLOR_ALT} />
          </g>
        );
      })}
    </svg>
  );
}

function MiniBarLinePreview() {
  const bars = [0.5, 0.7, 0.4, 0.65, 0.55, 0.8, 0.45];
  const linePoints = [0.7, 0.6, 0.75, 0.5, 0.65, 0.45, 0.8];
  const pts = linePoints.map((h, i) => `${6 + i * 11},${36 - h * 32}`).join(" ");
  return (
    <svg viewBox="0 0 80 36" className="chart-tpl-svg">
      {bars.map((h, i) => (
        <rect key={i} x={2 + i * 11} y={36 - h * 32} width={8} height={h * 32} rx={1.5} fill={BAR_COLOR} opacity={0.85} />
      ))}
      <polyline points={pts} fill="none" stroke={LINE_COLOR} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      {linePoints.map((h, i) => (
        <circle key={i} cx={6 + i * 11} cy={36 - h * 32} r={2} fill={LINE_COLOR} />
      ))}
    </svg>
  );
}

function MiniStackedPreview() {
  const data = [
    [0.3, 0.25, 0.2],
    [0.35, 0.3, 0.15],
    [0.2, 0.35, 0.25],
    [0.3, 0.2, 0.3],
    [0.25, 0.3, 0.2],
    [0.35, 0.25, 0.25],
    [0.2, 0.3, 0.3],
  ];
  return (
    <svg viewBox="0 0 80 36" className="chart-tpl-svg">
      {data.map((segs, i) => {
        let y = 36;
        return segs.map((h, si) => {
          const height = h * 36;
          y -= height;
          return <rect key={`${i}-${si}`} x={2 + i * 11} y={y} width={8} height={height} rx={si === segs.length - 1 ? 1.5 : 0} fill={STACK_COLORS[si]} />;
        });
      })}
    </svg>
  );
}

function ScratchPreview() {
  return (
    <div className="chart-tpl-scratch-icon">
      <ToolOutlined />
    </div>
  );
}

const TEMPLATES: ChartTemplateOption[] = [
  {
    key: "simple-bar",
    label: "Simple Bar",
    description: "Single metric as a bar chart",
    icon: <BarChartOutlined />,
    preview: <MiniBarPreview />,
  },
  {
    key: "simple-line",
    label: "Simple Line",
    description: "Single metric as a line chart",
    icon: <LineChartOutlined />,
    preview: <MiniLinePreview />,
  },
  {
    key: "bar-and-line",
    label: "Bar + Line",
    description: "Two metrics: one bar, one line overlay",
    icon: <LineChartOutlined />,
    preview: <MiniBarLinePreview />,
  },
  {
    key: "stacked-bar",
    label: "Stacked Bar",
    description: "Three metrics stacked in a single bar",
    icon: <BuildOutlined />,
    preview: <MiniStackedPreview />,
  },
  {
    key: "dual-bar",
    label: "Dual Bar",
    description: "Two metrics as side-by-side bars",
    icon: <BarChartOutlined />,
    preview: <MiniDualBarPreview />,
  },
  {
    key: "scratch",
    label: "Start from Scratch",
    description: "Build a custom chart from a blank canvas",
    icon: <ToolOutlined />,
    preview: <ScratchPreview />,
  },
];

interface ChartTemplatePickerProps {
  onSelect: (key: ChartTemplateKey | "scratch") => void;
}

export function ChartTemplatePicker({ onSelect }: ChartTemplatePickerProps) {
  return (
    <div className="chart-tpl-picker">
      <p className="awm-step-subtitle">Choose a chart starting point</p>
      <div className="chart-tpl-grid">
        {TEMPLATES.map((t) => (
          <button
            key={t.key}
            className="chart-tpl-card"
            onClick={() => onSelect(t.key)}
          >
            <div className="chart-tpl-preview">{t.preview}</div>
            <span className="chart-tpl-label">{t.label}</span>
            <span className="chart-tpl-desc">{t.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
